import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Snackbar, Alert, Typography } from "@mui/material";
import { Navbar } from "./components/Navbar";
import { BoardView } from "./components/Board/BoardView";
import { DealsTable } from "./components/Views/DealsTable";
import { CompaniesTable } from "./components/Views/CompaniesTable";
import { ContactsTable } from "./components/Views/ContactsTable";
import { AppointmentsTable } from "./components/Views/AppointmentsTable";
import {
  DealModal,
  StageModal,
  CompanyModal,
  ContactModal,
  DeleteConfirmDialog,
} from "./components/Modals";
import { DealDrawer } from "./components/DealDrawer";
import { useLeadData } from "./hooks/useLeadData";
import { useDragDrop } from "./hooks/useDragDrop";
import { VIEW_TYPES } from "./utils/constants";
import LeadFilters from "./components/LeadFilters"; // New

const LeadManagementSystem = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isViewer = userRole === "viewer";
  // View state lives in the URL (?view=list) so coming back from a lead
  // reopens the same view
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = searchParams.get("view");
  const currentView = Object.values(VIEW_TYPES).includes(urlView)
    ? urlView
    : VIEW_TYPES.BOARD;
  const setCurrentView = (view) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (view === VIEW_TYPES.BOARD) next.delete("view");
        else next.set("view", view);
        next.delete("page"); // list paging belongs to the view being left
        return next;
      },
      { replace: true }
    );
  const [selectedDeal, setSelectedDeal] = useState(null);

  // Modal states
  const [showDealModal, setShowDealModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDeleteStageConfirm, setShowDeleteStageConfirm] = useState(false);
  const [showDeleteDealConfirm, setShowDeleteDealConfirm] = useState(false);


  // Edit states
  const [editingStage, setEditingStage] = useState(null);
  const [stageToDelete, setStageToDelete] = useState(null);

  // Toast state
  const [toast, setToast] = useState({ message: "", type: "success" });

  // Custom hooks
  const {
    deals,
    totalDeals,
    boardColumns,
    loadMoreStage,
    fetchAllDeals,
    listPagination,
    stages,
    companies,
    contacts,
    appointments,
    notes,
    loading,
    filters, // New
    setFilters, // New
    fetchDeals,
    dealOperations,
    stageOperations,
    companyOperations,
    contactOperations,
    noteOperations,
    pipeline,
    setPipeline,
  } = useLeadData({ view: currentView });

  // Toast helper
  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  // ✅ Auto-refresh every 3 minutes (fetchDeals keeps the active filters)
  useEffect(() => {
    const interval = setInterval(() => {
      console.log("Auto-refreshing deals...");
      fetchDeals();
    }, 180000); // 3 minutes

    return () => clearInterval(interval);
  }, [fetchDeals]);

  // Drag and Drop
  const handleDrop = async (dealId, targetStageId) => {
    if (isViewer) {
      showToast("Viewers cannot move leads.", "error");
      return;
    }
    const result = await dealOperations.move(dealId, targetStageId);
    if (result.success) {
      showToast("Deal moved successfully!");
    } else {
      showToast("Failed to update stage!", "error");
    }
  };

  const {
    handleDragStart,
    handleDragOver,
    handleDrop: onDrop,
  } = useDragDrop(handleDrop);

  // Deal Handlers
  const handleCreateDeal = async (values, { setSubmitting, resetForm }) => {
    const selectedCompany = companies.find((c) => c.name === values.company);
    const selectedContact = contacts.find(
      (c) => `${c.firstName} ${c.lastName}` === values.contactName
    );
    const selectedStage = stages.find((s) => s?._id === values.stage);

    const payload = {
      dealName: values.dealName,
      company: selectedCompany?._id || "",
      companyName: values.company || "",
      contact: selectedContact?._id || "",
      stage: selectedStage?._id || "",
      amount: Number(values.amount) || "",
      closingDate: values.closingDate || "",
      priority: values.priority || "",
      source: values.source || "",
      description: values.description,
      mobile: values.mobile,
      email: values.email || "",
      // Contact / company details from the form (previously dropped here)
      whatsappNumber: values.whatsappNumber || "",
      linkedinUrl: values.linkedinUrl || "",
      jobTitle: values.jobTitle || "",
      primaryIndustry: values.primaryIndustry || "",
      country: values.country || "",
      city: values.city || "",
      interest: values.interest || "",
      subInterest: values.subInterest || "",
      // Complaint Fields
      loggedBy: values.loggedBy || "",
      callerName: values.callerName || "",
      paymentType: values.paymentType || "",
      machineNumber: values.machineNumber || "",
      reasonForContact: values.reasonForContact || "",
      complaintType: values.complaintType || "",
      errorCode: values.errorCode || "",
      juicesPushed: values.juicesPushed || "",
      creditNoteJuices: values.creditNoteJuices || "",
      commandCode: values.commandCode || "",
      complaintStatus: values.complaintStatus || "",
      refundAmount: values.refundAmount || "",
      customerEmailAddress: values.customerEmailAddress || "",
      complaintComments: values.complaintComments || "",
      // Rider / Gazmin Fields
      riderId: values.riderId || "",
      riderName: values.riderName || "",
      riderPlatform: values.riderPlatform || "",
      bikeNo: values.bikeNo || "",
      visaNo: values.visaNo || "",
      currentStore: values.currentStore || "",
      previousStore: values.previousStore || "",
      riderStatus: values.riderStatus || "",
      // Tatweer Fields
      area: values.area || "",
      buildingName: values.buildingName || "",
      unitNo: values.unitNo || "",
      serviceCategory: values.serviceCategory || "",
      requestType: values.requestType || "",
      existingTicketId: values.existingTicketId || "",
      preferredDateTime: values.preferredDateTime || "",
      // left undefined (not sent) by forms without a remarks field, so edits don't wipe it
      remarks: values.remarks,
    };
    // "Assign to" picked in the form; empty → the API decides (creator / auto-assign)
    if (values.assignee) payload.assignee = values.assignee;
    console.log(payload, "payload");

    const result = await dealOperations.create(payload);

    if (result.success) {
      showToast("Deal created successfully!");
      setShowDealModal(false);
      resetForm();
    } else {
      const errorMsg = result.error?.response?.data?.message || result.error?.message || "Failed to create deal!";
      showToast(errorMsg, "error");
    }

    setSubmitting(false);
  };

  const handleUpdateDeal = async (values, { setSubmitting }) => {
    if (isViewer) {
      showToast("Viewers cannot edit leads.", "error");
      return { success: false };
    }
    console.log(values, "-0-0-");
    const selectedCompany = companies.find((c) => c.name === values.company);
    const selectedContact = contacts.find(
      (c) => `${c.firstName} ${c.lastName}` === values.contactName
    );
    const selectedStage = stages.find(
      (s) => s._id === values.stage || s.id === values.stage
    );

    const payload = {
      dealName: values.dealName,
      company: selectedCompany?._id || "",
      companyName: values.company || "",
      contact: selectedContact?._id || "",
      stage: selectedStage?._id || "",
      amount: Number(values.amount),
      closingDate: values.closingDate,
      priority: values.priority,
      source: values.source,
      description: values.description,
      mobile: values.mobile,
      email: values.email || "",
      // Contact / company details from the form (previously dropped here)
      whatsappNumber: values.whatsappNumber || "",
      linkedinUrl: values.linkedinUrl || "",
      jobTitle: values.jobTitle || "",
      primaryIndustry: values.primaryIndustry || "",
      country: values.country || "",
      city: values.city || "",
      interest: values.interest || "",
      subInterest: values.subInterest || "",
      // Complaint Fields
      loggedBy: values.loggedBy || "",
      callerName: values.callerName || "",
      paymentType: values.paymentType || "",
      machineNumber: values.machineNumber || "",
      reasonForContact: values.reasonForContact || "",
      complaintType: values.complaintType || "",
      errorCode: values.errorCode || "",
      juicesPushed: values.juicesPushed || "",
      creditNoteJuices: values.creditNoteJuices || "",
      commandCode: values.commandCode || "",
      complaintStatus: values.complaintStatus || "",
      refundAmount: values.refundAmount || "",
      customerEmailAddress: values.customerEmailAddress || "",
      complaintComments: values.complaintComments || "",
      // Rider / Gazmin Fields
      riderId: values.riderId || "",
      riderName: values.riderName || "",
      riderPlatform: values.riderPlatform || "",
      bikeNo: values.bikeNo || "",
      visaNo: values.visaNo || "",
      currentStore: values.currentStore || "",
      previousStore: values.previousStore || "",
      riderStatus: values.riderStatus || "",
      // Tatweer Fields
      area: values.area || "",
      buildingName: values.buildingName || "",
      unitNo: values.unitNo || "",
      serviceCategory: values.serviceCategory || "",
      requestType: values.requestType || "",
      existingTicketId: values.existingTicketId || "",
      preferredDateTime: values.preferredDateTime || "",
      // left undefined (not sent) by forms without a remarks field, so edits don't wipe it
      remarks: values.remarks,
    };

    const result = await dealOperations.update(values.id, payload);

    if (result.success) {
      setSelectedDeal(result.deal);
      showToast("Deal updated successfully!");
      return { success: true };
    } else {
      const errorMsg = result.error?.response?.data?.message || result.error?.message || "Failed to update deal!";
      showToast(errorMsg, "error");
      return { success: false };
    }
  };

  const handleDeleteDeal = async () => {
    if (!selectedDeal) return;
    if (isViewer) {
      showToast("Viewers cannot delete leads.", "error");
      setShowDeleteDealConfirm(false);
      return;
    }

    const result = await dealOperations.delete(selectedDeal.id);

    if (result.success) {
      setSelectedDeal(null);
      showToast("Deal deleted successfully!");
    } else {
      showToast("Failed to delete deal!", "error");
    }

    setShowDeleteDealConfirm(false);
  };

  const handleCreateStage = async (values, { setSubmitting, resetForm }) => {
    const result = await stageOperations.create(values);

    if (result.success) {
      showToast("Stage created successfully!");
      setShowStageModal(false);
      resetForm();
    } else {
      showToast("Failed to create stage!", "error");
    }

    setSubmitting(false);
  };

  const handleUpdateStage = async (values, { setSubmitting }) => {
    const result = await stageOperations.update(values._id, values);

    if (result.success) {
      showToast("Stage updated successfully!");
      setShowStageModal(false);
      setEditingStage(null);
    } else {
      showToast("Failed to update stage!", "error");
    }

    setSubmitting(false);
  };

  const handleEditStage = (stage) => {
    setEditingStage(stage);
    setShowStageModal(true);
  };

  const handleDeleteStageClick = (stage) => {
    setStageToDelete(stage);
    setShowDeleteStageConfirm(true);
  };

  const handleDeleteStageConfirm = async () => {
    if (stageToDelete) {
      const result = await stageOperations.delete(stageToDelete._id);

      if (result.success) {
        showToast(result.message || "Stage deleted successfully!");
      } else {
        const errorMsg =
          result.error?.response?.data?.message || "Failed to delete stage!";
        showToast(errorMsg, "error");
      }
    }

    setShowDeleteStageConfirm(false);
    setStageToDelete(null);
  };

  // Company Handlers
  const handleCreateCompany = async (values, { setSubmitting, resetForm }) => {
    const result = await companyOperations.create(values);

    if (result.success) {
      showToast("Company added successfully!");
      setShowCompanyModal(false);
      resetForm();
    } else {
      showToast("Failed to add company!", "error");
    }

    setSubmitting(false);
  };

  // Contact Handlers
  const handleCreateContact = async (values, { setSubmitting, resetForm }) => {
    const selectedCompany = companies.find((c) => c.name === values.company);

    if (!selectedCompany) {
      showToast("Please select a valid company!", "error");
      setSubmitting(false);
      return;
    }

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      company: selectedCompany._id,
    };

    const result = await contactOperations.create(payload);

    if (result.success) {
      showToast("Contact added successfully!");
      setShowContactModal(false);
      resetForm();
    } else {
      showToast("Failed to add contact!", "error");
    }

    setSubmitting(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // height: "100vh",
      }}
    >
      {/* Navbar */}
      <Navbar
        currentView={currentView}
        onViewChange={setCurrentView}
        onAddDeal={() => setShowDealModal(true)}
        onAddCompany={() => setShowCompanyModal(true)}
        onAddContact={() => setShowContactModal(true)}
        onRefreshDeals={() => fetchDeals(filters)}
        totalDeals={totalDeals}
        fetchAllDeals={fetchAllDeals}
        pipeline={pipeline}
        onPipelineChange={(p) => navigate(`/lead-management-system?pipeline=${p}`)}
      />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{
          flexGrow: 1,
          overflow: "auto",
          p: 3,
          // Hide horizontal scrollbar
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}>
          {/* Inline Filter Bar */}
          <LeadFilters
            filters={filters}
            setFilters={setFilters}
            userId={userId}
            stages={stages}
          />

          {currentView === VIEW_TYPES.BOARD && (
            <BoardView
              stages={stages}
              columns={boardColumns}
              onLoadMore={loadMoreStage}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={onDrop}
              onDealClick={setSelectedDeal}
              onEditStage={handleEditStage}
              onDeleteStage={handleDeleteStageClick}
              onAddStage={() => {
                setEditingStage(null);
                setShowStageModal(true);
              }}
            />
          )}

          {currentView === VIEW_TYPES.LIST && (
            <>
              {listPagination.total === 0 ? (
                <Box
                  sx={{
                    width: "95%",
                    minHeight: 200,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#F9FAFB",
                    border: "2px dashed #D1D5DB",
                    borderRadius: 2,
                    color: "#6B7280",
                    textAlign: "center",
                    p: 3,
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    No data available in the Deals list.
                  </Typography>
                </Box>
              ) : (
                <DealsTable
                  deals={deals}
                  stages={stages}
                  onViewDeal={setSelectedDeal}
                  pagination={listPagination}
                />
              )}
            </>
          )}

          {currentView === VIEW_TYPES.APPOINTMENTS && (
            <AppointmentsTable
              leads={deals
                .filter((d) => d.followUpDate)
                .sort((a, b) => new Date(b.followUpDate) - new Date(a.followUpDate))}
              onViewLead={setSelectedDeal}
            />
          )}
        </Box>
      </Box>

      {/* Deal Modal */}
      <DealModal
        open={showDealModal}
        onClose={() => setShowDealModal(false)}
        onSubmit={handleCreateDeal}
        companies={companies}
        contacts={contacts}
        stages={stages}
        onAddCompany={() => setShowCompanyModal(true)}
        onAddContact={() => setShowContactModal(true)}
      />

      {/* Stage Modal */}
      <StageModal
        open={showStageModal}
        onClose={() => {
          setShowStageModal(false);
          setEditingStage(null);
        }}
        onSubmit={editingStage ? handleUpdateStage : handleCreateStage}
        editMode={!!editingStage}
        initialData={editingStage}
      />

      {/* Company Modal */}
      <CompanyModal
        open={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        onSubmit={handleCreateCompany}
      />

      {/* Contact Modal */}
      <ContactModal
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSubmit={handleCreateContact}
        companies={companies}
      />

      {/* Delete Stage Confirmation */}
      <DeleteConfirmDialog
        open={showDeleteStageConfirm}
        onClose={() => setShowDeleteStageConfirm(false)}
        onConfirm={handleDeleteStageConfirm}
        itemName={stageToDelete?.name}
      />

      {/* Delete Deal Confirmation */}
      <DeleteConfirmDialog
        open={showDeleteDealConfirm}
        onClose={() => setShowDeleteDealConfirm(false)}
        onConfirm={handleDeleteDeal}
        title="Confirm Deletion"
        message="Are you sure you want to delete this deal? This action cannot be undone."
      />

      {/* Deal Detail Drawer */}
      <DealDrawer
        open={!!selectedDeal}
        deal={selectedDeal}
        stages={stages}
        companies={companies}
        contacts={contacts}
        notes={noteOperations.getByDeal(selectedDeal?.id)}
        onClose={() => setSelectedDeal(null)}
        onUpdate={handleUpdateDeal}
        onDelete={() => setShowDeleteDealConfirm(true)}
        onAddNote={noteOperations.add}
        onDeleteNote={noteOperations.delete}
      />

      {/* Toast Notifications */}
      <Snackbar
        open={!!toast.message}
        autoHideDuration={3000}
        onClose={() => setToast({ message: "", type: "success" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast({ message: "", type: "success" })}
          severity={toast.type}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadManagementSystem;
